// D3 Heatmap Chart for BodyOS data visualizations
// Perfect for HRV by age, supplement timing, etc.
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'
import {
  createSvg,
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  debounce
} from './utils'

export default class HeatmapChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { ...defaultMargins, left: 110 },
      xLabel: '',
      yLabel: '',
      colorScheme: 'grayscale', // grayscale, diverging, sequential
      showValues: true,
      showLegend: true,
      animate: true,
      responsive: true,
      cellPadding: 2,
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  getColorScale(domain) {
    const [min, max] = domain

    switch (this.options.colorScheme) {
      case 'diverging':
        return d3.scaleSequential()
          .domain([min, max])
          .interpolator(d3.interpolateRdYlGn)

      case 'sequential':
        return d3.scaleSequential()
          .domain([min, max])
          .interpolator(d3.interpolateBlues)

      case 'grayscale':
      default:
        // Custom grayscale that matches BodyOS design
        return d3.scaleLinear()
          .domain([min, (min + max) / 2, max])
          .range([colors.neutral[200], colors.neutral[500], colors.neutral[900]])
    }
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)
    const height = (width / this.options.width) * this.options.height

    // Extract unique x and y values
    const xValues = [...new Set(this.data.map((d) => d.x))].sort((a, b) => {
      if (typeof a === 'number') return a - b
      return a.localeCompare(b)
    })
    const yValues = [...new Set(this.data.map((d) => d.y))].sort((a, b) => {
      if (typeof a === 'number') return a - b
      return a.localeCompare(b)
    })

    // Calculate left margin based on longest Y-axis label
    const maxYLabelLength = Math.max(...yValues.map((y) => String(y).length))
    // Approximately 7px per character for monospace font at size 10, plus padding for axis label
    const estimatedYLabelWidth = maxYLabelLength * 7 + 30
    const margins = { ...this.options.margins }
    margins.left = Math.max(margins.left, estimatedYLabelWidth)
    // Store for Y-axis label positioning
    this.leftMargin = margins.left

    const { svg, g, innerWidth, innerHeight } = createSvg(
      this.container,
      width,
      height,
      margins
    )
    this.svg = svg

    this.tooltip = createTooltip(this.container)

    // Create scales
    const xScale = d3.scaleBand()
      .domain(xValues)
      .range([0, innerWidth])
      .padding(0.05)

    const yScale = d3.scaleBand()
      .domain(yValues)
      .range([0, innerHeight])
      .padding(0.05)

    const valueExtent = d3.extent(this.data, (d) => d.value)
    const colorScale = this.getColorScale(valueExtent)

    // X axis
    const xAxis = g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale))

    xAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    xAxis.selectAll('line, path')
      .attr('stroke', 'none')

    if (this.options.xLabel) {
      g.append('text')
        .attr('class', 'x-axis-label')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + 45)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.xLabel.toUpperCase())
    }

    // Y axis
    const yAxis = g.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(yScale))

    yAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    yAxis.selectAll('line, path')
      .attr('stroke', 'none')

    if (this.options.yLabel) {
      // Position Y-axis label based on calculated left margin (leave room for tick labels)
      const yLabelOffset = this.leftMargin - 15
      g.append('text')
        .attr('class', 'y-axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -yLabelOffset)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.yLabel.toUpperCase())
    }

    // Cells
    const cells = g.selectAll('.cell')
      .data(this.data)
      .enter()
      .append('g')
      .attr('class', 'cell')
      .attr('transform', (d) => `translate(${xScale(d.x)}, ${yScale(d.y)})`)

    const rects = cells.append('rect')
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .attr('fill', (d) => colorScale(d.value))
      .attr('stroke', colors.neutral.white)
      .attr('stroke-width', this.options.cellPadding)
      .attr('rx', 2)
      .style('cursor', 'pointer')

    if (this.options.animate) {
      rects
        .attr('opacity', 0)
        .transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * 20)
        .attr('opacity', 1)
    }

    // Cell values
    if (this.options.showValues && xScale.bandwidth() > 30) {
      cells.append('text')
        .attr('x', xScale.bandwidth() / 2)
        .attr('y', yScale.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .attr('fill', (d) => {
          const bgColor = colorScale(d.value)
          return this.getContrastColor(bgColor)
        })
        .attr('font-size', fonts.sizes.xs)
        .attr('font-family', fonts.family)
        .attr('font-weight', fonts.weights.medium)
        .text((d) => this.formatCellValue(d))
    }

    // Tooltip interactions
    cells
      .on('mouseover', (event, d) => {
        d3.select(event.currentTarget).select('rect')
          .transition()
          .duration(200)
          .attr('stroke', colors.neutral[900])
          .attr('stroke-width', 3)

        const content = this.formatTooltip(d)
        showTooltipHtml(this.tooltip, content, event)
      })
      .on('mouseout', (event) => {
        d3.select(event.currentTarget).select('rect')
          .transition()
          .duration(200)
          .attr('stroke', colors.neutral.white)
          .attr('stroke-width', this.options.cellPadding)
        hideTooltip(this.tooltip)
      })

    // Legend
    if (this.options.showLegend) {
      this.renderLegend(g, colorScale, valueExtent, innerWidth, innerHeight)
    }

    return this
  }

  formatCellValue(d) {
    // If there's a label (like ">65 ms"), use that
    if (d.label) {
      return d.label
    }
    // Otherwise format the numeric value
    return this.formatValue(d.value)
  }

  formatValue(value) {
    const { formatValue } = this.options
    if (typeof formatValue === 'function') {
      return formatValue(value)
    }
    if (typeof formatValue === 'string') {
      // Treat string as a suffix (e.g., "ms" -> "55 ms")
      return `${value} ${formatValue}`
    }
    return value
  }

  formatTooltip(d) {
    // Show label if available, otherwise format the value
    const displayValue = d.label || this.formatValue(d.value)
    let html = `<strong>${d.y}</strong> × <strong>${d.x}</strong><br/>`
    html += `<span style="color: ${colors.accent}">${displayValue}</span>`
    return html
  }

  renderLegend(g, colorScale, extent, innerWidth, _innerHeight) {
    const legendWidth = 200
    const legendHeight = 12
    const legendX = innerWidth - legendWidth
    const legendY = -25

    // Gradient for legend
    const defs = this.svg.select('defs').empty() ?
      this.svg.append('defs') :
      this.svg.select('defs')

    const gradientId = 'heatmap-legend-gradient'
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('x2', '100%')

    const colorStops = 10
    for (let i = 0; i <= colorStops; i += 1) {
      const t = i / colorStops
      const value = extent[0] + t * (extent[1] - extent[0])
      gradient.append('stop')
        .attr('offset', `${t * 100}%`)
        .attr('stop-color', colorScale(value))
    }

    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${legendX}, ${legendY})`)

    legend.append('rect')
      .attr('width', legendWidth)
      .attr('height', legendHeight)
      .attr('fill', `url(#${gradientId})`)
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 1)

    // Legend labels
    legend.append('text')
      .attr('x', 0)
      .attr('y', legendHeight + 14)
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.xs)
      .attr('font-family', fonts.family)
      .text(this.options.formatValue ? this.options.formatValue(extent[0]) : extent[0])

    legend.append('text')
      .attr('x', legendWidth)
      .attr('y', legendHeight + 14)
      .attr('text-anchor', 'end')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.xs)
      .attr('font-family', fonts.family)
      .text(this.options.formatValue ? this.options.formatValue(extent[1]) : extent[1])
  }

  getContrastColor(bgColor) {
    // Simple luminance check
    const rgb = d3.color(bgColor)
    if (!rgb) return colors.text
    const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b
    return luminance > 150 ? colors.neutral[900] : colors.neutral.white
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
